import * as React from "react";
import {
  createBitMaskValue,
  findIndexes,
  calculateMaxValue,
  updateMask,
  createHex
} from "../../utils/utils";

export type BitMaskFieldProps = {
  id: string;
  label: string;
  subLabel: string;
  maskValue: string | "";
  onMaskChange: (value: string, offset: number) => string | number;
  disabled?: boolean;
  isError?: boolean;
};

export type BitMaskFieldState = {
  bitMaskValue: string[];
  firstIndex: number;
  lastIndex: number;
  maxValue: string;
};

class BitMaskField extends React.Component<BitMaskFieldProps, BitMaskFieldState> {
  constructor(props: BitMaskFieldProps) {
    super(props);
    this.onBitToggle = this.onBitToggle.bind(this);
    this.state = {
      bitMaskValue: [],
      firstIndex: 0,
      lastIndex: 0,
      maxValue: ""
    };
  }

  componentDidMount(): void {
    const bitMask = createBitMaskValue(this.props.maskValue);
    const { first, last } = findIndexes(bitMask);
    const max = calculateMaxValue(bitMask, first, last);
    this.setState({
      bitMaskValue: bitMask,
      firstIndex: first,
      lastIndex: last,
      maxValue: max.toString()
    });
  }

  onBitToggle = (index: number): void => {
    const { bitMaskValue, firstIndex, lastIndex } = this.state;
    if (index === firstIndex && index < lastIndex) {
      const newMask = bitMaskValue;
      newMask[index] = "0";
      const max = calculateMaxValue(newMask, index + 1, lastIndex);
      this.setState({ bitMaskValue: newMask, firstIndex: index + 1, maxValue: max.toString() });
      const updated = createHex(newMask, index + 1);
      this.props.onMaskChange(updated, 31 - lastIndex);
    } else if (index === firstIndex && index === lastIndex && index >= 0) {
      const newMask = bitMaskValue;
      newMask[index] = "0";
      this.setState({ bitMaskValue: newMask, firstIndex: -1, lastIndex: -1, maxValue: "0" });
      const updated = createHex(newMask, index);
      this.props.onMaskChange(updated, 33);
    } else if (firstIndex < 0 && lastIndex < 0 && index >= 0) {
      const temp = bitMaskValue;
      temp[index] = "1";
      const { first, last } = findIndexes(temp);
      const newMask = updateMask(temp, first, last);
      const max = calculateMaxValue(newMask, first, last);
      this.setState({ bitMaskValue: newMask, firstIndex: first, lastIndex: last, maxValue: max.toString() });
      const updated = createHex(newMask, first);
      this.props.onMaskChange(updated, 31 - last);
    } else if (index === lastIndex && index > firstIndex) {
      const temp = bitMaskValue;
      temp[index] = "0";
      const { first, last } = findIndexes(temp);
      const newMask = updateMask(temp, first, last);
      const max = calculateMaxValue(newMask, firstIndex, index - 1);
      this.setState({ bitMaskValue: newMask, lastIndex: last, maxValue: max.toString() });
      const updated = createHex(newMask, first);
      this.props.onMaskChange(updated, 31 - last);
    } else if (index < firstIndex && index >= 0) {
      const newMask = updateMask(bitMaskValue, index, lastIndex);
      const max = calculateMaxValue(newMask, index, lastIndex);
      this.setState({ bitMaskValue: newMask, firstIndex: index, maxValue: max.toString() });
      const updated = createHex(newMask, index);
      this.props.onMaskChange(updated, 31 - lastIndex);
    } else if (index > lastIndex && index <= 31) {
      const newMask = updateMask(bitMaskValue, firstIndex, index + 1);
      const max = calculateMaxValue(newMask, firstIndex, index);
      this.setState({ bitMaskValue: newMask, lastIndex: index, maxValue: max.toString() });
      const updated = createHex(newMask, firstIndex);
      this.props.onMaskChange(updated, 31 - index);
    }
  };

  render(): JSX.Element {
    const { bitMaskValue, firstIndex, lastIndex, maxValue } = this.state;
    const { label, subLabel, id } = this.props;
    return (
      <div className="bit-mask-field" id={id}>
        <div className="mask-field-label">{label}</div>
        <div className="mask-bits" id="bits_container">
          {bitMaskValue.map((item, index) => (
            <button
              disabled={index > firstIndex && index < lastIndex}
              className="bit-value"
              onClick={() => this.onBitToggle(index)}
              key={index}
            >
              {item}
            </button>
          ))}
        </div>
        <div className="mask-field-label">{subLabel}</div>
        <div className="value-range">
          <p className="ranges">
            <span>0 ...</span>
            <span>{maxValue ? maxValue : "0"}</span>
          </p>
        </div>
      </div>
    );
  }
}

export default BitMaskField;

